import { useCallback, useState } from 'react';
import { useApi, useMutation } from './useApi';
import {
  getReportDefinitions,
  getReportDefinition,
  createReportDefinition,
  updateReportDefinition,
  deleteReportDefinition,
  getReportExecutions,
  getReportExecution,
  runReport,
  ReportDefinitionFilters,
  ReportExecutionFilters,
  RunReportResponse,
} from '../services/api';
import {
  ReportDefinition,
  ReportExecution,
  PaginatedResponse,
  CreateReportDefinitionRequest,
  UpdateReportDefinitionRequest,
} from '../types';

export function useReportDefinitions(initialFilters: ReportDefinitionFilters = {}) {
  const [filters, setFilters] = useState<ReportDefinitionFilters>(initialFilters);

  const fetchDefinitions = useCallback(() => getReportDefinitions(filters), [filters]);

  const { data, loading, error, refetch } = useApi<PaginatedResponse<ReportDefinition>>(
    fetchDefinitions,
    [JSON.stringify(filters)]
  );

  return {
    definitions: data?.items || [],
    total: data?.total || 0,
    page: data?.page || 1,
    totalPages: data?.totalPages || 1,
    loading,
    error,
    filters,
    setFilters,
    refetch,
  };
}

export function useReportDefinition(id: string | null) {
  const fetchDefinition = useCallback(() => {
    if (!id) return Promise.resolve(null);
    return getReportDefinition(id);
  }, [id]);

  const { data, loading, error, refetch } = useApi<ReportDefinition | null>(
    fetchDefinition,
    [id]
  );

  return {
    definition: data,
    loading,
    error,
    refetch,
  };
}

export function useCreateReportDefinition() {
  const { mutate, loading, error, data, reset } = useMutation<
    ReportDefinition,
    CreateReportDefinitionRequest
  >(createReportDefinition);

  return {
    createDefinition: mutate,
    loading,
    error,
    createdDefinition: data,
    reset,
  };
}

export function useUpdateReportDefinition() {
  const { mutate, loading, error, data, reset } = useMutation<
    ReportDefinition,
    { id: string; data: UpdateReportDefinitionRequest }
  >(({ id, data }) => updateReportDefinition(id, data));

  return {
    updateDefinition: mutate,
    loading,
    error,
    updatedDefinition: data,
    reset,
  };
}

export function useDeleteReportDefinition() {
  const { mutate, loading, error, data, reset } = useMutation<
    { success: boolean; message: string },
    string
  >(deleteReportDefinition);

  return {
    deleteDefinition: mutate,
    loading,
    error,
    result: data,
    reset,
  };
}

export function useReportExecutions(initialFilters: ReportExecutionFilters = {}) {
  const [filters, setFilters] = useState<ReportExecutionFilters>(initialFilters);

  const fetchExecutions = useCallback(() => getReportExecutions(filters), [filters]);

  const { data, loading, error, refetch } = useApi<PaginatedResponse<ReportExecution>>(
    fetchExecutions,
    [JSON.stringify(filters)]
  );

  return {
    executions: data?.items || [],
    total: data?.total || 0,
    page: data?.page || 1,
    totalPages: data?.totalPages || 1,
    loading,
    error,
    filters,
    setFilters,
    refetch,
  };
}

export function useReportExecution(id: string | null) {
  const fetchExecution = useCallback(() => {
    if (!id) return Promise.resolve(null);
    return getReportExecution(id);
  }, [id]);

  const { data, loading, error, refetch } = useApi<ReportExecution | null>(
    fetchExecution,
    [id]
  );

  return {
    execution: data,
    loading,
    error,
    refetch,
  };
}

export function useRunReport() {
  const { mutate, loading, error, data, reset } = useMutation<
    RunReportResponse,
    { definitionId: string; triggeredByUser?: string }
  >(({ definitionId, triggeredByUser }) => runReport(definitionId, triggeredByUser));

  return {
    runReport: mutate,
    loading,
    error,
    result: data,
    reset,
  };
}
